import * as React from 'react'
import * as SheetPrimitive from '@radix-ui/react-dialog'
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { SheetCloseContext, SheetContainerContext } from './sheet-hooks'

function SheetContainerProvider({
  container,
  children,
}: {
  container: HTMLElement | null
  children: React.ReactNode
}) {
  return <SheetContainerContext value={container}>{children}</SheetContainerContext>
}

function Sheet({
  onOpenChange,
  modal,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Root>) {
  const close = React.useCallback(() => onOpenChange?.(false), [onOpenChange])
  const container = React.useContext(SheetContainerContext)
  const effectiveModal = container ? false : (modal ?? undefined)

  return (
    <SheetCloseContext value={close}>
      <SheetPrimitive.Root onOpenChange={onOpenChange} modal={effectiveModal} {...props} />
    </SheetCloseContext>
  )
}

const SheetTrigger = SheetPrimitive.Trigger

const SheetClose = SheetPrimitive.Close

const SheetPortal = SheetPrimitive.Portal

const OVERLAY_BASE =
  'inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0'

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay> & { contained?: boolean }
>(({ className, contained, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn(contained ? 'absolute' : 'fixed', OVERLAY_BASE, className)}
    {...props}
    ref={ref}
  />
))
SheetOverlay.displayName = 'SheetOverlay'

const sheetVariants = cva(
  'z-50 gap-4 bg-background p-6 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out',
  {
    variants: {
      side: {
        top: 'inset-x-0 top-0 border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top',
        bottom:
          'inset-x-0 bottom-0 border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom',
        left: 'inset-y-0 left-0 h-full w-3/4 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left sm:max-w-sm',
        right:
          'inset-y-0 right-0 h-full w-3/4 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-sm',
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
)

interface SheetContentProps
  extends React.PropsWithChildren<React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>>,
    VariantProps<typeof sheetVariants> {
  /** Hide the built-in top-right close (X) — e.g. when a Cancel button already
   *  lives in the sheet's own header. */
  hideClose?: boolean
}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = 'right', className, children, hideClose = false, ...props }, ref) => {
  const container = React.useContext(SheetContainerContext)
  const isContained = !!container

  return (
    <SheetPortal container={container ?? undefined}>
      {isContained ? <div className="absolute inset-0 z-50 bg-black/50" /> : <SheetOverlay />}
      <SheetPrimitive.Content
        ref={ref}
        className={cn(isContained ? 'absolute' : 'fixed', sheetVariants({ side }), className)}
        onInteractOutside={
          isContained
            ? (e) => {
                // Don't close the sheet when clicking the chat drawer (outside the container)
                const target = e.target as HTMLElement | null
                if (target && container && !container.contains(target)) {
                  e.preventDefault()
                }
              }
            : undefined
        }
        onFocusOutside={
          isContained
            ? (e) => {
                // Don't steal focus from the chat drawer
                const target = e.target as HTMLElement | null
                if (target && container && !container.contains(target)) {
                  e.preventDefault()
                }
              }
            : undefined
        }
        {...props}
      >
        {!hideClose && (
          <SheetPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-secondary">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
})
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex flex-col space-y-2 text-center sm:text-left', className)} {...props} />
)
SheetHeader.displayName = 'SheetHeader'

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2', className)}
    {...props}
  />
)
SheetFooter.displayName = 'SheetFooter'

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    ref={ref}
    className={cn('text-lg font-semibold text-foreground', className)}
    {...props}
  />
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
  SheetContainerProvider,
}
